import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import { useAuthContext } from '../contexts/AuthContext';
import {
  useActiveAnnouncement,
  useDismissAnnouncement,
} from '../hooks/api/useAnnouncements';
import { useToast } from '../hooks/useToast';
import { AlertToast } from './AlertToast';
import { ModalDialog } from './ModalDialog';
import { RichTextContent } from './RichTextContent';

/**
 * The latest "What's New" announcement, for every logged-in account, the
 * superadmin included. Announcements are written in English, hence `lang="en"`.
 */
export const WhatsNewBanner = () => {
  const { t } = useTranslation();
  const { loggedInAccount } = useAuthContext();
  const { data } = useActiveAnnouncement(!!loggedInAccount);
  const dismiss = useDismissAnnouncement();
  const errorToast = useToast(5000);

  const [open, setOpen] = useState(false);
  // Hidden for the rest of the session once dismissed, even if saving that failed.
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const sectionRef = useRef<HTMLElement>(null);

  const announcement = data?.announcement;
  const visible =
    !!announcement && !data?.seen && dismissedId !== announcement.id;

  const handleDismiss = async () => {
    if (!announcement || dismiss.isPending) return;
    setOpen(false);
    setStatusMessage(t('announcements.banner.dismissing') as string);
    try {
      await dismiss.mutateAsync(announcement.id);
      setStatusMessage(t('announcements.banner.dismissed') as string);
    } catch {
      setStatusMessage('');
      errorToast.trigger();
    }
    // The banner is about to unmount: don't leave focus on a removed element.
    const hadFocus = sectionRef.current?.contains(document.activeElement);
    setDismissedId(announcement.id);
    if (hadFocus) {
      requestAnimationFrame(() =>
        document.getElementById('main-content')?.focus()
      );
    }
  };

  return (
    <>
      {/* Outside the banner so "Dismissed" is still announced once it's gone. */}
      <span role="status" className="sr-only">
        {statusMessage}
      </span>
      {errorToast.show && (
        <AlertToast severity="error">
          {t('announcements.banner.dismissError')}
        </AlertToast>
      )}
      {visible && (
        <>
          <Box
            component="section"
            ref={sectionRef}
            aria-label={t('announcements.banner.label') as string}
            className="box-border"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              gap: 12,
              width: '100%',
              mb: 16,
              py: 8,
              pl: 16,
              pr: 8,
              backgroundColor: 'var(--color-light-greyish-lime-green)',
              border: '1px solid var(--color-light-greyish-lime-green)',
              borderRadius: 1,
              color: 'var(--color-deep-ocean-blue)',
            }}
          >
            <CampaignIcon
              aria-hidden="true"
              sx={{ color: 'var(--color-scifree-teal)', flexShrink: 0 }}
            />
            <Box
              component="p"
              sx={{
                flex: 1,
                minWidth: 0,
                m: 0,
                fontWeight: 500,
                overflowWrap: 'anywhere',
              }}
            >
              <span className="sr-only">
                {t('announcements.banner.prefix')}
              </span>
              <span lang="en">{announcement.title}</span>
            </Box>
            <Button
              variant="primary"
              size="small"
              onClick={() => setOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              {t('announcements.banner.readMore')}
            </Button>
            <IconButton
              aria-label={t('announcements.banner.dismiss') as string}
              aria-busy={dismiss.isPending || undefined}
              // aria-disabled keeps focus on the button while the request runs
              aria-disabled={dismiss.isPending || undefined}
              onClick={handleDismiss}
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                color: 'var(--color-deep-ocean-blue)',
              }}
            >
              {dismiss.isPending ? (
                <CircularProgress
                  size={18}
                  aria-hidden="true"
                  sx={{ color: 'var(--color-deep-ocean-blue)' }}
                />
              ) : (
                <CloseIcon aria-hidden="true" />
              )}
            </IconButton>
          </Box>
          <ModalDialog
            open={open}
            onClose={() => setOpen(false)}
            title={announcement.title}
            actions={
              <Button
                variant="primary"
                onClick={handleDismiss}
                aria-disabled={dismiss.isPending || undefined}
              >
                {t('announcements.banner.gotIt')}
              </Button>
            }
          >
            <RichTextContent html={announcement.body_html} lang="en" />
          </ModalDialog>
        </>
      )}
    </>
  );
};
